import vine, {
  BaseLiteralType,
  SimpleMessagesProvider,
  VineBoolean,
  VineDate,
  VineNumber,
  type symbols,
} from '@vinejs/vine'
import type { FieldContext, InferInput } from '@vinejs/vine/types'
import { DateTime } from 'luxon'
import {
  MATRICULE_INTERIM_PREFIX,
  MATRICULE_SUBCONTRACTING_PREFIX,
  PG_INTEGER_MAX,
} from '../constants.js'
import { clothingSize, clothingType } from '../enums/clothing.js'
import { collaboratorGender } from '../enums/collaborator-gender.js'
import {
  collaboratorRole,
  rolesWithoutMandatoryManager,
  type CollaboratorRole,
} from '../enums/collaborator-role.js'
import { collaboratorStatus } from '../enums/collaborator-status.js'
import {
  type ContractType,
  contractType,
  hasEmploymentContract,
  salariedContractType,
} from '../enums/contract-type.js'
import { emailField } from './email.js'
import { INVALID_EMAIL_MESSAGE } from './messages.js'
import { equipmentType } from '../enums/equipment-type.js'
import { professionalStatus } from '../enums/professional-status.js'
import { unavailabityReason } from '../enums/unavailabity-reason.js'
import { weekDays, type WeekDay } from '../enums/week-days.js'

// Point de vérité unique de la validation collaborateur (backend AdonisJS + back-office). Deep-import
// uniquement (`@gozoki/api-types/src/validation/collaborator`) — JAMAIS ré-exporté par le barrel
// (`index.ts`). Ce module importe `vine` (runtime) ; via le barrel, Metro l'embarquerait dans le
// bundle de l'app Expo et le build EAS échouerait (`Unable to resolve module node:dns/promises`
// depuis @vinejs/vine). L'app RN n'a besoin que des types, pas de vine.

const DATE_FORMATS = ['iso8601', 'YYYY-MM-DD']
export const TIME_REGEX = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/
const MATRICULE_REGEX = /^\d+$/

// `.parse()` : les valeurs de formulaire arrivent telles quelles (objets `Date`, `null`). On ramène
// `Date` en ISO et `null` en `undefined` (pour que `requiredWhen` se déclenche). Côté backend les
// valeurs sont déjà des chaînes : la transformation est transparente.
const toDateInput = (value: unknown) => {
  if (value == null) return undefined
  return value instanceof Date ? DateTime.fromJSDate(value).toISODate() : value
}
const nullToUndefined = (value: unknown) => value ?? undefined

// @ts-expect-error override volontaire : on remplace le type d'entrée `string | number` par `Date`.
class VineJsDate extends VineDate {} // eslint-disable-line no-unsafe-declaration-merging -- override type-only (cf. interface ci-dessous)
// @ts-expect-error override volontaire : on remplace le type d'entrée `string | number` par `Date`.
interface VineJsDate {
  [symbols.ITYPE]: Date
}

// Champ date acceptant une `Date` JS *ou* une chaîne (ISO / `YYYY-MM-DD`) en entrée.
export const jsDate = () => new VineJsDate({ formats: [...DATE_FORMATS] }).parse(toDateInput)

const dateField = jsDate
const wireDate = () => vine.date({ formats: [...DATE_FORMATS] })

class VineJsNumber extends VineNumber {} // eslint-disable-line no-unsafe-declaration-merging -- override type-only (cf. interface ci-dessous)
interface VineJsNumber {
  [symbols.ITYPE]: number
}

// Nombre dont le TYPE d'entrée est `number` (runtime inchangé, coercition conservée).
export const jsNumber = () => new VineJsNumber()

class VineJsBoolean extends VineBoolean {} // eslint-disable-line no-unsafe-declaration-merging -- override type-only (cf. interface ci-dessous)
interface VineJsBoolean {
  [symbols.ITYPE]: boolean
}

// Booléen dont le TYPE d'entrée est `boolean` (runtime inchangé, coercition conservée).
export const jsBoolean = () => new VineJsBoolean()

type NonNullableOptional<Schema extends BaseLiteralType<unknown, unknown, unknown>> = Omit<
  ReturnType<Schema['optional']>,
  typeof symbols.ITYPE | typeof symbols.OTYPE
> & {
  // `| undefined` ⊆ `| null | undefined` : narrowing (on garde `undefined`, on jette `null`).
  [symbols.ITYPE]: NonNullable<ReturnType<Schema['optional']>[typeof symbols.ITYPE]> | undefined
  [symbols.OTYPE]: NonNullable<ReturnType<Schema['optional']>[typeof symbols.OTYPE]> | undefined
}

declare module '@vinejs/vine' {
  // Les params génériques doivent être IDENTIQUES (mêmes noms) à ceux de la classe pour que la
  // fusion de déclarations opère — on ne peut donc pas les préfixer `_`, d'où le disable ciblé.
  // eslint-disable-next-line no-unused-vars
  interface BaseLiteralType<Input, Output, CamelCaseOutput> {
    nonNullableOptional(): NonNullableOptional<this>
  }
}

// Runtime : c'est un `.optional()` normal (il accepte `undefined`) ; seul le TYPE est resserré.
// `.macro` est statique et attend un constructeur concret ; `BaseLiteralType` est abstraite → cast.
;(
  BaseLiteralType as unknown as {
    macro(
      name: 'nonNullableOptional',
      fn: (this: BaseLiteralType<unknown, unknown, unknown>) => unknown,
    ): void
  }
).macro('nonNullableOptional', function nonNullableOptional() {
  return this.optional()
})
const phoneField = () => vine.string().mobile({ locale: ['fr-FR'] })
const nameField = () => vine.string().trim().minLength(1)
/** Heure locale `HH:MM` (24 h) : format horaire partagé par tous les modules. */
export const timeField = () => vine.string().trim().regex(TIME_REGEX)

/** Valeur brute d'un champ voisin, lue par une règle de champ : ni trim ni conversion appliqués. */
export const parentValue = (field: FieldContext, key: string) =>
  (field.parent as Record<string, unknown>)[key]

export const managerRequired = (field: FieldContext) =>
  !rolesWithoutMandatoryManager.includes(parentValue(field, 'role') as CollaboratorRole)

export const storeIdsRequired = (field: FieldContext) =>
  parentValue(field, 'mainStoreId') !== undefined

export const mainStoreIdRequired = (field: FieldContext) =>
  parentValue(field, 'storeIds') !== undefined

/**
 * Matricule saisi et champs du contrat de travail requis, sauf pour un type qui n'en a pas
 * (`hasEmploymentContract` : ni l'intérim ni la sous-traitance). Un type pas encore choisi, ou
 * invalide (refusé par sa propre règle), les exige comme un CDI.
 */
const requiresEmploymentContract = (typeKey: 'type' | 'contractType') => (field: FieldContext) => {
  const type = parentValue(field, typeKey)
  return !contractType.includes(type as ContractType) || hasEmploymentContract(type as ContractType)
}

export const mainStoreInStoresRule = vine.createRule((value, _options, field) => {
  const storeIds = parentValue(field, 'storeIds')
  if (value == null || !Array.isArray(storeIds)) return
  const ids = storeIds.map((id) => Number(id))
  if (!ids.includes(Number(value))) {
    field.report(
      "L'affectation de référence doit faire partie des affectations",
      'mainStoreInStores',
      field,
    )
  }
})

// Photo requise sauf si une photo existe déjà (mode édition). Règle implicite : elle s'exécute même
// quand le champ est absent. `File` n'existe pas côté backend, d'où la garde.
const profilePictureRequiredRule = vine.createRule(
  (value, _options, field) => {
    const hasNewFile = typeof File !== 'undefined' && value instanceof File
    const hasExisting =
      Boolean(parentValue(field, 'profilePictureUrl')) &&
      parentValue(field, 'removeProfilePicture') !== true
    if (!hasNewFile && !hasExisting) {
      field.report('Photo de profil requise', 'profilePictureRequired', field)
    }
  },
  { implicit: true },
)

const workTimeSlotRule = vine.createRule((value, _options, field) => {
  const slot = value as { startTime?: string; endTime?: string }
  if (slot?.startTime && slot?.endTime && slot.startTime >= slot.endTime) {
    field.report('Le créneau doit avoir un début avant la fin', 'workTimeSlot', field)
  }
})

const workTimeOverlapRule = vine.createRule((value, _options, field) => {
  if (!Array.isArray(value)) return
  for (let i = 1; i < value.length; i++) {
    const previous = value[i - 1]
    const current = value[i]
    // Ordre chronologique d'abord (créneaux triés par heure de début croissante)…
    if (previous.startTime >= current.startTime) {
      field.report('Les créneaux doivent être chronologiques', 'workTimeOverlap', field)
      return
    }
    // …puis absence de chevauchement (le précédent doit finir avant le début du suivant).
    if (previous.endTime > current.startTime) {
      field.report('Les créneaux ne doivent pas se chevaucher', 'workTimeOverlap', field)
      return
    }
  }
})

// Matricule CDD/CDI (formulaire) : requis et uniquement des chiffres. Règle implicite (message
// spécifique, et évite le conflit de la clé `regex` avec les champs horaires).
const matriculeDigitsRule = vine.createRule(
  (value, _options, field) => {
    if (!requiresEmploymentContract('type')(field)) return
    if (!value) {
      field.report('Le matricule est requis', 'matricule', field)
    } else if (!MATRICULE_REGEX.test(value as string)) {
      field.report('Matricule requis (chiffres uniquement)', 'matricule', field)
    }
  },
  { implicit: true },
)

// Absence d'un seul jour avec heures : le début doit précéder la fin. `field.parent` porte les
// valeurs brutes (objets `Date`).
const singleDayTimeRule = vine.createRule((value, _options, field) => {
  const startDate = parentValue(field, 'startDate')
  const endDate = parentValue(field, 'endDate')
  const startTime = parentValue(field, 'startTime')
  if (!startDate || !endDate || !startTime || !value) return
  const sameDay = +new Date(startDate as string) === +new Date(endDate as string)
  if (sameDay && (startTime as string) >= (value as string)) {
    field.report(
      'Le temps de début doit être avant le temps de fin pour une absence à un jour',
      'singleDayTime',
      field,
    )
  }
})

/** Une semaine entière : borne haute d'une durée de travail hebdomadaire. */
export const MAX_WEEKLY_WORKING_MINUTES = 7 * 24 * 60

/** La même borne en heures, pour les formulaires qui saisissent la durée en heures. */
export const MAX_WEEKLY_WORKING_HOURS = MAX_WEEKLY_WORKING_MINUTES / 60

/** Durée hebdomadaire en minutes, au plus une semaine (collaborateur et contrats). */
export const weeklyWorkingMinutesField = () => vine.number().min(0).max(MAX_WEEKLY_WORKING_MINUTES)

/**
 * Matricule saisi (CDI, CDD) : pas aux préfixes des matricules générés (`AGDI` intérim, `AGDS`
 * sous-traitance), pour ne pas prendre un numéro de leur séquence.
 */
const enteredMatriculeRule = vine.createRule((value, _options, field) => {
  // Intérim, sous-traitance : la valeur reçue est ignorée, le matricule généré.
  if (typeof value !== 'string' || !requiresEmploymentContract('contractType')(field)) return
  const generatedPrefixes = [MATRICULE_INTERIM_PREFIX, MATRICULE_SUBCONTRACTING_PREFIX]
  if (generatedPrefixes.some((prefix) => value.toUpperCase().startsWith(prefix))) {
    field.report('Ce préfixe est réservé aux matricules générés', 'enteredMatricule', field)
  }
})

export const collaboratorFieldRules = vine.object({
  firstName: nameField(),
  lastName: nameField(),
  email: emailField(),
  phone: phoneField(),
  role: vine.enum(collaboratorRole),
  // Contrats salariés seulement : la sous-traitance ne se crée que depuis la fiche logistique.
  contractType: vine.enum(salariedContractType),
  status: vine.enum(collaboratorStatus),
  gender: vine.enum(collaboratorGender),
  // Au plus une semaine entière : au-delà, la colonne entière de Postgres refuserait (500).
  weeklyWorkingMinutes: weeklyWorkingMinutesField(),
  hourlyCost: vine.number().min(0).max(PG_INTEGER_MAX).withoutDecimals(),
  managerId: vine.number(),
  interimAgencyId: vine.number(),
  storeIds: vine.array(vine.number()),
  mainStoreId: vine.number(),
  matricule: vine.string().trim(),
  birthDate: wireDate(),
  professionalStatus: vine.enum(professionalStatus),
  entryDate: wireDate(),
  seniorityDate: wireDate(),
  trialPeriodEndDate: wireDate(),
  startDate: wireDate(),
  endDate: wireDate(),
  comments: vine.string().nullable(),
})

export const collaboratorClientFields = () => {
  const f = collaboratorFieldRules.getProperties()
  return {
    ...f,
    managerId: f.managerId.optional().requiredWhen(managerRequired),
    interimAgencyId: f.interimAgencyId.optional().requiredWhen('contractType', '=', 'interim'),
    storeIds: f.storeIds.minLength(1).optional().requiredWhen(storeIdsRequired),
    mainStoreId: f.mainStoreId
      .use(mainStoreInStoresRule())
      .optional()
      .requiredWhen(mainStoreIdRequired),
    //#region optionnels
    matricule: f.matricule
      .use(enteredMatriculeRule())
      .optional()
      .requiredWhen(requiresEmploymentContract('contractType')),
    birthDate: f.birthDate.optional(),
    professionalStatus: f.professionalStatus
      .optional()
      .requiredWhen(requiresEmploymentContract('contractType')),
    entryDate: f.entryDate.optional().requiredWhen(requiresEmploymentContract('contractType')),
    seniorityDate: f.seniorityDate.optional(),
    trialPeriodEndDate: f.trialPeriodEndDate.optional(),
    startDate: f.startDate.optional().requiredWhen(requiresEmploymentContract('contractType')),
    endDate: f.endDate.afterOrSameAs('startDate').optional(),
    comments: f.comments.optional(),
    //#endregion
  }
}
//#endregion

export const collaboratorIdentitySchema = vine.object({
  ...collaboratorFieldRules.pick(['firstName', 'lastName', 'email', 'phone', 'status']),
  gender: vine.enum(collaboratorGender).parse(nullToUndefined),
  storeIds: vine.array(jsNumber()).minLength(1),
  mainStoreId: jsNumber().parse(nullToUndefined).use(mainStoreInStoresRule()),
  profilePicture: vine.any().optional().use(profilePictureRequiredRule()),
  //#region optionnels
  birthDate: dateField().nullable().optional(),
  //#endregion
})

export const collaboratorContractSchema = vine.object({
  // `type` = `contractType` du leaf mais renommé (pick ne renomme pas) → reste explicite.
  type: vine.enum(salariedContractType),
  ...collaboratorFieldRules.pick(['role']),
  // Mêmes bornes que l'API (`weeklyWorkingMinutesField`, `hourlyCost`), dites dès la saisie.
  weeklyWorkingHours: jsNumber().min(1).max(MAX_WEEKLY_WORKING_HOURS),
  hourlyCost: jsNumber().min(1).max(PG_INTEGER_MAX).withoutDecimals(),
  //#region optionnels
  matricule: vine.string().trim().use(matriculeDigitsRule()),
  managerId: jsNumber().parse(nullToUndefined).nullable().optional().requiredWhen(managerRequired),
  interimAgencyId: jsNumber()
    .parse(nullToUndefined)
    .nullable()
    .optional()
    .requiredWhen('type', '=', 'interim'),
  startDate: dateField().nullable().optional().requiredWhen(requiresEmploymentContract('type')),
  endDate: dateField().afterOrSameAs('startDate').nullable().optional(),
  professionalStatus: vine
    .enum(professionalStatus)
    .parse(nullToUndefined)
    .nullable()
    .optional()
    .requiredWhen(requiresEmploymentContract('type')),
  entryDate: dateField().nullable().optional().requiredWhen(requiresEmploymentContract('type')),
  seniorityDate: dateField().nullable().optional(),
  trialPeriodEndDate: dateField().nullable().optional(),
  comments: vine.string().nonNullableOptional(),
  //#endregion
})

// Fabrique (une instance fraîche par jour : on ne partage pas un même schéma de tableau entre les 7
// champs d'un même objet).
const availabilityDaySlots = () =>
  vine
    .array(vine.object({ startTime: timeField(), endTime: timeField() }).use(workTimeSlotRule()))
    .use(workTimeOverlapRule())

export const collaboratorAvailabilitiesSchema = vine.object({
  monday: availabilityDaySlots(),
  tuesday: availabilityDaySlots(),
  wednesday: availabilityDaySlots(),
  thursday: availabilityDaySlots(),
  friday: availabilityDaySlots(),
  saturday: availabilityDaySlots(),
  sunday: availabilityDaySlots(),
})

export const collaboratorGoodsSchema = vine.object({
  objects: vine.array(
    vine.object({
      type: vine.enum(equipmentType),
      givenDate: dateField(),
      serialNumber: vine.string().trim().minLength(1),
      brand: vine.string().trim().minLength(1),
      //#region optionnels
      id: jsNumber().nonNullableOptional(),
      //#endregion
    }),
  ),
  clothes: vine.array(
    vine.object({
      type: vine.enum(clothingType),
      size: vine.enum(clothingSize),
      quantity: jsNumber().min(1),
      //#region optionnels
      id: jsNumber().nonNullableOptional(),
      //#endregion
    }),
  ),
})

export const collaboratorUnavailabilitySchema = vine.object({
  collaboratorId: jsNumber(),
  reason: vine.enum(unavailabityReason),
  allDay: jsBoolean(),
  startDate: dateField(),
  endDate: dateField().afterOrSameAs('startDate'),
  //#region optionnels
  startTime: timeField().optional().requiredWhen('allDay', '=', false),
  endTime: timeField().use(singleDayTimeRule()).optional().requiredWhen('allDay', '=', false),
  //#endregion
})
//#endregion

//#region wire (backend) — sous-ressources
export const collaboratorWorkTimeFields = () => ({
  dayOfWeek: vine.enum(weekDays),
  startTime: timeField(),
  endTime: timeField(),
})

export const collaboratorUnavailabilityFields = () => ({
  startDate: dateField(),
  endDate: dateField(),
  reason: vine.enum(unavailabityReason),
  //#region optionnels
  startTime: timeField().optional(),
  endTime: timeField().optional(),
  //#endregion
})

export const collaboratorEquipmentFields = () => ({
  type: vine.enum(equipmentType),
  givenDate: dateField(),
  serialNumber: vine.string(),
  brand: vine.string(),
})

export const collaboratorClothingFields = () => ({
  type: vine.enum(clothingType),
  size: vine.enum(clothingSize),
  quantity: vine.number(),
})
//#endregion

//#region invariants wire work-times / unavailabilities (structure « lot »/« Luxon » du backend)
export const WORK_TIME_MESSAGES = {
  slotOrder: 'Chaque créneau doit avoir un début avant la fin',
  overlap: (a: number, b: number) => `Les créneaux ${a} et ${b} se chevauchent`,
}

interface WorkTimeItem {
  dayOfWeek: WeekDay
  startTime: string
  endTime: string
}

export const isWorkTimeSlotValid = (slot: { startTime: string; endTime: string }) =>
  slot.startTime < slot.endTime

const doWorkTimesOverlap = (a: WorkTimeItem, b: WorkTimeItem) =>
  a.dayOfWeek === b.dayOfWeek && a.startTime < b.endTime && b.startTime < a.endTime

export const findWorkTimesOverlap = (items: WorkTimeItem[]): [number, number] | null => {
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (doWorkTimesOverlap(items[i], items[j])) return [i, j]
    }
  }
  return null
}

export const UNAVAILABILITY_MESSAGES = {
  dateRange: 'La date de début doit être avant la date de fin',
  singleDayTime: 'Le temps de début doit être avant le temps de fin pour une absence à un jour',
}

export const isUnavailabilityDateRangeValid = (startDate: string, endDate: string) =>
  startDate <= endDate

export const hasValidSingleDayTimeRange = (
  startDate: string,
  endDate: string,
  startTime?: string | null,
  endTime?: string | null,
) => {
  if (startDate !== endDate || !startTime || !endTime) return true
  return startTime < endTime
}
//#endregion

//#region types dérivés — valeurs de FORMULAIRE = `InferInput` (l'entrée du schéma). Grâce à `jsDate`/
// `jsNumber`/`jsBoolean` et `.nonNullableOptional()`, l'entrée du schéma EST déjà exactement le type
// des valeurs du form (`Date`, `number`, `boolean`, `| undefined` sans `null`) — plus besoin de
// reconvertir la sortie `DateTime` en `Date`. Bonus : l'alignement form↔schéma est garanti par
// construction (les deux dérivent du même schéma).
export type CollaboratorIdentityFormValues = InferInput<typeof collaboratorIdentitySchema>
export type CollaboratorContractFormValues = InferInput<typeof collaboratorContractSchema>
export type CollaboratorAvailabilitiesFormValues = InferInput<
  typeof collaboratorAvailabilitiesSchema
>
export type CollaboratorGoodsFormValues = InferInput<typeof collaboratorGoodsSchema>
export type CollaboratorUnavailabilityFormValues = InferInput<
  typeof collaboratorUnavailabilitySchema
>
//#endregion

//#region Messages FR — front (`tryValidate`) et backend (`validateUsing`). La composition `vine.object`
// préfixe les champs (`identity.firstName`), donc on s'appuie sur des clés par RÈGLE (fallback qui
// marche quel que soit le préfixe) ; les messages vraiment spécifiques passent par des règles
// custom (message en dur). Quelques clés `champ.règle` restent pour le schéma wire (backend) et le
// formulaire d'absence autonome, où les champs ne sont pas préfixés.
/** Messages des règles de la fiche collaborateur, réutilisables par d'autres fiches qui les composent. */
export const collaboratorFrenchMessageMap = {
  required: 'Ce champ est obligatoire',
  string: 'Ce champ est invalide',
  number: 'Ce champ doit être un nombre',
  boolean: 'Valeur invalide',
  enum: 'Valeur invalide',
  email: INVALID_EMAIL_MESSAGE,
  mobile: 'Numéro de téléphone invalide',
  maxLength: 'Ce champ est trop long',
  minLength: 'Ce champ est obligatoire',
  min: 'La valeur est trop petite',
  max: 'La valeur est trop grande',
  regex: 'Format horaire invalide (HH:mm)',
  date: 'Date invalide',
  'date.format': 'Date invalide',
  'date.afterOrSameAs': 'La date de fin doit être postérieure ou égale à la date de début',
  'array.minLength': 'Sélectionnez au moins un élément',
  'hourlyCost.withoutDecimals': 'Le coût horaire doit être un nombre entier de centimes',
  'matricule.required': 'Le matricule est requis',
  'reason.enum': "Veuillez sélectionner un motif de l'absence",
  'collaboratorId.number': 'Veuillez sélectionner un collaborateur',
  // Deux jeux de clés selon le contexte :
  // - Contexte WRAPPÉ (`{ contract }` validé d'un bloc par la fiche vierge logistique,
  //   `routes/logistics/collaborator-creation.ts`) → chemins préfixés (`contract.*`).
  // - Contexte GROUPE (form d'ajout, `form.FormGroup name="identity"|"contract"`) → chemins relatifs.
  'gender.required': 'Sélectionnez un genre',
  'mainStoreId.required': 'Sélectionnez une affectation de référence',
  'contract.managerId.required': 'Sélectionnez un manager',
  'contract.startDate.required': 'Date de début de contrat requise',
  'contract.interimAgencyId.required': "Sélectionnez une agence d'intérim",
  'contract.professionalStatus.required': 'Sélectionnez un statut professionnel',
  'contract.entryDate.required': "Date d'entrée requise",
  'managerId.required': 'Sélectionnez un manager',
  'startDate.required': 'Date de début de contrat requise',
  'interimAgencyId.required': "Sélectionnez une agence d'intérim",
  'professionalStatus.required': 'Sélectionnez un statut professionnel',
  'entryDate.required': "Date d'entrée requise",
} satisfies Record<string, string>

export const collaboratorFrenchMessages = new SimpleMessagesProvider(collaboratorFrenchMessageMap)
//#endregion
